module API.Comments where

import Common

import API.Types (DocumentID)
import Data.Variant as Var
import GraphQL.Query (Mutation, buildMutation)
import GraphQL.Shape (Union, fromUnion)
import GraphQL.Type (NamedType, TypedID, namedType)
import Internal.Luxon (LDateTime)
import Record (modify)
import Type.Prelude (Proxy(..))

type CommentID = TypedID "Comment"

-- Fields shared by `Comment` and `CommentResponse`
type CommentBase r =
    { id :: CommentID
    , comment :: String
    , created_at :: LDateTime
    , updated_at :: LDateTime
    , public :: Boolean
    , can :: { update :: Boolean, delete :: Boolean }
    | r
    }

-- Final processed comment info
type Comment = CommentBase
    ( author :: { name :: String }
    )

-- Actual JSON response received from the backend
type CommentResponse = CommentBase
    ( author :: Union
        ( "Student" :: { name :: String }
        , "Teacher" :: { name :: String }
        )
    )

processComment :: CommentResponse -> Comment
processComment = modify (Proxy :: Proxy "author") processAuthor
  where
    processAuthor au = fromMaybe { name: "No Author" }
        (Var.match
            { "Student": \s -> { name: s.name }
            , "Teacher": \s -> { name: s.name }
            } <$> fromUnion au)

data CommentParent = FormComment DocumentID
type CommentInput =
    { comment :: String
    , public :: Boolean
    }

postComment :: CommentParent -> CommentInput -> Mutation Comment
postComment parent input = processComment <$> buildMutation "createComment"
    { parent_id: parent'.id
    , parent_type: parent'.type
    , data: namedType input :: NamedType "CommentInput" CommentInput
    }
  where
    parent' = case parent of
        FormComment id -> { id, type: namedType "FORM" :: NamedType "CommentParentType" String }

updateComment :: CommentID -> CommentInput -> Mutation Comment
updateComment id input = processComment <$> buildMutation "updateComment"
    { id, data: namedType input :: NamedType "CommentInput" CommentInput }

deleteComment :: CommentID -> Mutation Comment
deleteComment id = processComment <$> buildMutation "deleteComment" { id }
